import { generateRandomNonce, NONCE_LENGTH } from '../crypto-utils';
import { toUint8Array, fromUint8Array } from 'js-base64';

export enum RSAKeyFormat {
  SPKI = 'spki',
  PKCS8 = 'pkcs8',
}

export const toRSAKey = async (keyBytes: Uint8Array, format: RSAKeyFormat) => {
  const usage: KeyUsage[] =
    format === RSAKeyFormat.PKCS8 ? ['decrypt'] : ['encrypt'];
  return await window.crypto.subtle.importKey(
    format,
    keyBytes,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    usage
  );
};

export const encryptRSA = async (plainText: string, publicKey: Uint8Array) => {
  const key = await toRSAKey(publicKey, RSAKeyFormat.SPKI);
  const nonce = generateRandomNonce();
  const encrypted = new Uint8Array(
    await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
        iv: nonce,
      },
      key,
      toUint8Array(plainText)
    )
  );

  const fullMessage = new Uint8Array(nonce.length + encrypted.byteLength);
  fullMessage.set(nonce);
  fullMessage.set(new Uint8Array(encrypted), nonce.length);

  return fromUint8Array(fullMessage);
};

export const decryptRSA = async (
  cipherTextWithNonce: string,
  privateKey: Uint8Array
) => {
  const key = await toRSAKey(privateKey, RSAKeyFormat.PKCS8);
  const messageBytes = toUint8Array(cipherTextWithNonce);
  const nonce = messageBytes.slice(0, NONCE_LENGTH);
  const encrypted = messageBytes.slice(NONCE_LENGTH, messageBytes.length);

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP',
      iv: nonce,
    },
    key,
    encrypted
  );

  return new Uint8Array(decrypted);
};
